import create from 'zustand'
import { UseFormReturn } from 'react-hook-form'
import { ObjectiveDto, ObjectiveInput } from '../../../types'
import { TargetModalState, TargetModalFormErrors, TargetModalProps, TargetModalType } from './types'
import { NoteInfoInputExtended } from '../TargetTemplateModal/types'

const initialValues = {
  isModalOpened: false,
  targetModalType: TargetModalType.unknown,
  modalProps: {
    employeeId: '',
    meetingId: '',
    targetBonus: 0,
    hasBonusOrWillHaveBonus: false,
    allowedNewTargets: true,
    allowedPastTargets: true,
    disabledMonetaryButtonTooltip: '',
    publicProfile: false,
    country: null,
    employeeCurrency: undefined,
  },
  currentObjective: null,
  currencyConversionRate: undefined,
  currentAttachment: null,
  currentComments: [],
  commentFromInput: undefined,
  newTargetForm: null,
  newTargetFormErrors: null,
}

export const useTargetsModalStore = create<TargetModalState>((set) => ({
  ...initialValues,

  openTargetModal: (targetModalType: TargetModalType, objective?: ObjectiveDto, currencyConversionRate?: number) => {
    const objectiveInput = objective ? (({ appraisalSeason, ...rest }) => rest)(objective) : null

    set({
      isModalOpened: true,
      targetModalType,
      currentObjective: objectiveInput,
      currentAttachment: objectiveInput?.attachment ?? null,
      currentComments: objectiveInput?.noteList?.length ? objectiveInput.noteList : [],
      currencyConversionRate,
    })
  },

  closeTargetModal: () =>
    set({
      isModalOpened: false,
    }),

  cleanTargetModal: () =>
    set({
      newTargetForm: null,
      newTargetFormErrors: null,
      targetModalType: TargetModalType.unknown,
      currentObjective: null,
      currentAttachment: null,
      currentComments: [],
      commentFromInput: undefined,
      currencyConversionRate: undefined,
    }),

  setModalProps: (modalProps: TargetModalProps) => set({ modalProps }),

  cleanModalProps: () => set({ modalProps: initialValues.modalProps }),

  setNewTargetForm: (newTargetForm: UseFormReturn<ObjectiveInput>) => set({ newTargetForm }),

  setNewTargetFormErrors: (newTargetFormErrors: TargetModalFormErrors | null) => set({ newTargetFormErrors }),

  setAttachment: (attachment: string | null) => set({ currentAttachment: attachment }),

  addComment: (comment: NoteInfoInputExtended) =>
    set((state) => {
      const currentComments = state.currentComments.slice()
      currentComments.push(comment)
      return {
        ...state,
        currentComments,
        commentFromInput: undefined,
      }
    }),

  removeComment: (id: string, isNewComment = false) =>
    set((state) => {
      const currentComments = state.currentComments.filter((comment) => comment[isNewComment ? 'id' : 'globalId'] !== id)
      return {
        ...state,
        currentComments,
      }
    }),

  editComment: (id: string, text: string) =>
    set((state) => {
      const currentComments = state.currentComments.reduce((acc: Array<NoteInfoInputExtended>, current: NoteInfoInputExtended) => {
        if (current.id === id) {
          acc.push({ ...current, text })
        } else {
          acc.push(current)
        }
        return acc
      }, [])

      return {
        ...state,
        currentComments,
      }
    }),

  setCommentFromInput: (commentFromInput: string | undefined) => set({ commentFromInput }),
}))
